/**
 * Jellybean Interiors — Bundle Builder
 * jb-bundle-builder.js
 */

(function () {
  'use strict';

  /*
   * Shopify can retain the previous AU product-template route after a template
   * assignment changes. Keep this one product on the current default-template
   * render until that route cache is refreshed, while leaving every other
   * product and market untouched.
   */
  const isMoseyPlaymatRoute = window.location.pathname === '/products/forget-me-not-play-mat';
  const isAustralia = window.Shopify && window.Shopify.country === 'AU';
  const isThemeEditor = window.Shopify && window.Shopify.designMode;
  const routeParams = new URLSearchParams(window.location.search);

  if (isMoseyPlaymatRoute && isAustralia && !isThemeEditor && !routeParams.has('view')) {
    routeParams.set('view', 'default');
    window.location.replace(window.location.pathname + '?' + routeParams.toString() + window.location.hash);
    return;
  }

  /* ---- Colour map ---- */
  const COLOUR_MAP = {
    'watermelon sugar': '#fb83cd',
    'fairy floss': '#FFC0CA',
    'fairy bread': '#edebe6',
    'fairy bread white': '#edebe6',
    'blueberry': '#B9DBFB',
    'sage': '#BCD7A5',
    'ice': '#D4F9EE',
    'mint': '#A9F1BA',
    'lilac': '#DECFF5',
    'peach': '#ffdcc9',
    'sand': '#FFEFC0',
    'white': '#F8F8F8',
    'red': '#E05050',
    'blue fizz': '#C7EDFF',
    'fruit tingle': '#FFFFCC',
    'grape soda': '#E2CBFF',
    'sherbet orange': '#ff9636',
    'orange sherbet': '#ff9636',
    'sour apple': '#B4F99F',
    'Candy Apple': '#fc7678',
    'Chilli': '#fc7678',
    'Calypso': '#b5d662',
    'Bingo-ish': '#F6AB6D',
    'Blu Bluey': '#96D7EC',
    'Totally Rad': '#DEF8FC',
    'Honey': '#FFEA87',
    'Lucky Star': '#FFF5CC',
    'Snickers': '#855b58',
    'Captain': '#72213F'
  };

  const DISPLAY_NAMES = {
    'fairy bread': 'Fairy Bread',
    'sand': 'Sand',
    'fairy floss': 'Fairy Floss',
    'lilac': 'Lilac',
    'sage': 'Sage',
    'peach': 'Peach',
    'watermelon sugar': 'Watermelon Sugar',
    'blueberry': 'Blueberry',
    'ice': 'Ice',
    'mint': 'Mint',
    'fruit tingle': 'Fruit Tingle',
    'sour apple': 'Sour Apple',
    'blue fizz': 'Blue Fizz',
    'grape soda': 'Grape Soda',
    'orange sherbet': 'Orange Sherbet'
  };

  function normalizeColorKey(colorName) {
    const value = (colorName || '').toLowerCase().trim();
    return Object.keys(DISPLAY_NAMES).find(key => value.includes(key)) || value;
  }

  function getDisplayColorName(colorName) {
    const key = normalizeColorKey(colorName);
    return DISPLAY_NAMES[key] || colorName;
  }

  function getSwatchColor(colorName) {
    const key = (colorName || '').toLowerCase().trim();
    if (COLOUR_MAP[key]) return COLOUR_MAP[key];
    for (const [mapKey, val] of Object.entries(COLOUR_MAP)) {
      if (key.includes(mapKey) || mapKey.includes(key)) return val;
    }
    return '#cccccc';
  }

  const BUNDLE_STATUS_LABELS = {
    in_stock: 'In stock',
    preorder: 'Pre-order',
    out_of_stock: 'Out of stock'
  };

  function normalizeBundleStatus(status) {
    return Object.prototype.hasOwnProperty.call(BUNDLE_STATUS_LABELS, status) ? status : '';
  }

  function isProductSelectable(product) {
    if (!product || product.available !== true) return false;
    return normalizeBundleStatus(product.status) !== 'out_of_stock';
  }

  // Prices come from Liquid as cents (e.g. 25999 for $259.99)
  function formatMoney(cents) {
    const amount = (Math.round(cents) / 100).toFixed(2);
    return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ============================================================
     JBBundleBuilder
     ============================================================ */
  class JBBundleBuilder {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.sectionId;
      this.data = (window.JBBundleData && window.JBBundleData[this.sectionId]) || null;

      this.selections = { table: null, chair1: null, chair2: null };
      this.pendingPopup = null;

      /* DOM refs */
      this.popupEl        = el.querySelector('[data-popup]');
      this.backdropEl     = el.querySelector('[data-popup-backdrop]');
      this.popupImgEl     = el.querySelector('[data-popup-img]');
      this.popupNameEl    = el.querySelector('[data-popup-name]');
      this.popupSelectBtn = el.querySelector('[data-popup-select]');
      this.popupCloseBtn  = el.querySelector('[data-popup-close]');
      this.pricingEl      = el.querySelector('[data-pricing]');
      this.ctaBtn         = el.querySelector('[data-cta]');

      if (!this.data) {
        console.warn('[JBBundle] No data for section:', this.sectionId);
        return;
      }

      // Remove lazy-loading from popup img so images load immediately when src is set
      if (this.popupImgEl) this.popupImgEl.loading = 'eager';

      this.init();
    }

    init() {
      // Clear any stale placeholder text (e.g. "— not selected —") left by cache
      this.el.querySelectorAll('[data-selected-name]').forEach(el => {
        if (el.textContent.trim()) el.textContent = '';
      });
      this.renderSwatches('table',  this.data.tables);
      this.renderSwatches('chair1', this.data.chairs);
      this.renderSwatches('chair2', this.data.chairs);
      this.updatePricing();
      this.bindGlobalEvents();
      this.setupPreloader();
    }

    /* ---- Image preloader ---- */
    setupPreloader() {
      const allProducts = [...(this.data.tables || []), ...(this.data.chairs || [])];
      const urls = allProducts.map(p => p.image).filter(Boolean);
      if (!urls.length) return;

      // Start preloading 200px before the section scrolls into view
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this._preloadImages(urls);
            observer.disconnect();
          }
        });
      }, { rootMargin: '200px' });

      observer.observe(this.el);
    }

    _preloadImages(urls) {
      urls.forEach(url => {
        const img = new Image();
        img.src = url;
      });
    }

    getSelectorImage(type, product) {
      const imageMap = this.data.selectorImages || {};
      const group = type === 'table' ? 'table' : 'chair';
      const colorKey = normalizeColorKey(product.colorName || product.title);
      return (imageMap[group] && imageMap[group][colorKey]) || '';
    }

    /* ---- Render swatches ---- */
    renderSwatches(type, products) {
      const container = this.el.querySelector(`[data-swatches="${type}"]`);
      if (!container || !products || !products.length) return;
      container.innerHTML = '';

      products.forEach(product => {
        const colorKey = normalizeColorKey(product.colorName || product.title);
        const displayName = getDisplayColorName(product.colorName || product.title);
        const selectorImage = this.getSelectorImage(type, product);
        const bg = getSwatchColor(colorKey);
        const status = normalizeBundleStatus(product.status);
        const statusLabel = BUNDLE_STATUS_LABELS[status] || '';
        const isSelectable = isProductSelectable(product);

        // Wrapper div — handles sale badge + swatch stacking
        const wrap = document.createElement('div');
        wrap.className = 'jb-swatch-wrap';
        if (product.onSale) wrap.dataset.sale = 'true';

        // Sale badge — always present so all wraps are equal height;
        // CSS hides it via visibility:hidden when not on sale
        const badge = document.createElement('span');
        badge.className = 'jb-swatch-sale';
        badge.textContent = 'SALE';
        wrap.appendChild(badge);

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'jb-swatch';
        btn.style.setProperty('--jb-swatch-colour', bg);
        if (selectorImage) {
          btn.classList.add('jb-swatch--photo');

          if (status) {
            const statusBadge = document.createElement('span');
            statusBadge.className = `jb-swatch-status jb-swatch-status--${status}`;
            statusBadge.textContent = statusLabel;
            statusBadge.setAttribute('aria-hidden', 'true');
            btn.appendChild(statusBadge);
          }

          const image = document.createElement('img');
          image.className = 'jb-swatch__image';
          image.src = selectorImage;
          image.alt = '';
          image.width = 120;
          image.height = 100;
          image.loading = 'lazy';
          image.decoding = 'async';
          image.setAttribute('aria-hidden', 'true');
          btn.appendChild(image);

          const label = document.createElement('span');
          label.className = 'jb-swatch__label';
          label.textContent = displayName;
          btn.appendChild(label);
        } else {
          btn.style.backgroundColor = bg;
        }
        btn.dataset.variantId = product.variantId;
        btn.dataset.productId = product.id;
        btn.dataset.colorKey  = colorKey;
        btn.dataset.available = isSelectable;
        btn.dataset.status    = status;
        btn.dataset.selected  = 'false';
        btn.title = displayName;
        btn.setAttribute('aria-label', 'Select ' + displayName + ' ' + (type === 'table' ? 'table' : 'chair') + (statusLabel ? '. ' + statusLabel : ''));
        btn.setAttribute('aria-pressed', 'false');

        if (!isSelectable) btn.disabled = true;

        // Desktop hover keeps the image preview available. A tap/click selects
        // immediately at every size so mobile shoppers can build a set in 3 taps.
        btn.addEventListener('mouseenter', () => {
          if (window.innerWidth > 640) this.showPopup(type, product, btn);
        });
        btn.addEventListener('mouseleave', () => {
          if (window.innerWidth > 640) {
            this._hideTimer = setTimeout(() => this.hidePopup(), 250);
          }
        });
        btn.addEventListener('click', () => {
          if (!isProductSelectable(product)) return;
          this.selectProduct(type, product);
          this.hidePopup();
        });

        wrap.appendChild(btn);
        container.appendChild(wrap);
      });
    }

    /* ---- Popup: fixed panel in top-right of section on desktop ---- */
    showPopup(type, product, swatchEl) {
      clearTimeout(this._hideTimer);
      this.pendingPopup = { type, product, swatchEl };

      const previewImage = this.getSelectorImage(type, product) || product.image;
      const displayName = getDisplayColorName(product.colorName || product.title);
      const isSelectable = isProductSelectable(product);

      if (previewImage) {
        this.popupImgEl.src = previewImage;
        this.popupImgEl.alt = displayName;
        this.popupImgEl.style.display = '';
      } else {
        this.popupImgEl.style.display = 'none';
      }
      this.popupNameEl.textContent = displayName;
      if (!this.popupSelectBtn.dataset.defaultText) {
        this.popupSelectBtn.dataset.defaultText = this.popupSelectBtn.textContent;
      }
      this.popupSelectBtn.disabled = !isSelectable;
      this.popupSelectBtn.textContent = isSelectable
        ? this.popupSelectBtn.dataset.defaultText
        : 'Out of stock';

      this.popupEl.classList.add('is-visible');
      this.popupEl.setAttribute('aria-hidden', 'false');

      if (window.innerWidth <= 640) {
        this.backdropEl.classList.add('is-visible');
      } else {
        // Position popup to the right of the hovered selector row
        var selectorRow = swatchEl.closest('[data-selector]');
        var rect = (selectorRow || swatchEl).getBoundingClientRect();
        var popupW = 220;
        var popupH = this.popupEl.offsetHeight || 300;
        var top = rect.top + (rect.height / 2) - (popupH / 2);
        top = Math.max(8, Math.min(top, window.innerHeight - popupH - 8));
        var left = rect.right + 12;
        // Flip to left side if no room on right
        if (left + popupW > window.innerWidth - 8) {
          left = rect.left - popupW - 12;
        }
        this.popupEl.style.top  = top + 'px';
        this.popupEl.style.left = left + 'px';
      }
    }

    hidePopup() {
      clearTimeout(this._hideTimer);
      this.popupEl.classList.remove('is-visible');
      this.popupEl.setAttribute('aria-hidden', 'true');
      this.backdropEl.classList.remove('is-visible');
      this.popupEl.style.top  = '';
      this.popupEl.style.left = '';
      this.pendingPopup = null;
    }

    bindGlobalEvents() {
      // Keep popup open when hovering over it
      this.popupEl.addEventListener('mouseenter', () => clearTimeout(this._hideTimer));
      this.popupEl.addEventListener('mouseleave', () => {
        this._hideTimer = setTimeout(() => this.hidePopup(), 250);
      });

      // Select button inside popup
      this.popupSelectBtn.addEventListener('click', () => {
        if (this.pendingPopup && isProductSelectable(this.pendingPopup.product)) {
          this.selectProduct(this.pendingPopup.type, this.pendingPopup.product);
          this.hidePopup();
        }
      });

      this.popupCloseBtn.addEventListener('click', () => this.hidePopup());
      this.backdropEl.addEventListener('click',   () => this.hidePopup());
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.hidePopup(); });

      this.ctaBtn.addEventListener('click', () => this.addToCart());

      document.addEventListener('shopify:section:load', e => {
        if (e.detail && e.detail.sectionId === this.sectionId) this.init();
      });
    }

    /* ---- Select product ---- */
    selectProduct(type, product) {
      this.selections[type] = product;

      // Update swatch ring
      const container = this.el.querySelector(`[data-swatches="${type}"]`);
      if (container) {
        container.querySelectorAll('.jb-swatch').forEach(btn => {
          const sel = btn.dataset.variantId == product.variantId;
          btn.dataset.selected = sel ? 'true' : 'false';
          btn.setAttribute('aria-pressed', sel ? 'true' : 'false');
        });
      }

      // Update inline colour name
      const nameEl = this.el.querySelector(`[data-selected-name="${type}"]`);
      if (nameEl) nameEl.textContent = getDisplayColorName(product.colorName || product.title);

      this.updatePricing();
      this.updateCTA();
    }

    /* ---- Pricing ---- */
    updatePricing() {
      if (!this.pricingEl) return;
      const { table, chair1, chair2 } = this.selections;
      const settings = this.data.settings;

      if (!table && !chair1 && !chair2) {
        this.pricingEl.innerHTML = `<p class="jb-bundle__price-incomplete">Select your table and 2 chairs to see bundle price</p>`;
        return;
      }

      // Prices are decimal dollars from Liquid (e.g. 259.99)
      const tablePrice  = table  ? Math.round(table.price)  : 0;
      const chair1Price = chair1 ? Math.round(chair1.price) : 0;
      const chair2Price = chair2 ? Math.round(chair2.price) : 0;
      const subtotal    = tablePrice + chair1Price + chair2Price;

      const discountPct = parseFloat(settings.discountPct) || 0;
      const discounted  = subtotal * (1 - discountPct / 100);
      const savings     = subtotal - discounted;

      let html = `<div class="jb-bundle__price-row">`;
      html += `<span class="jb-bundle__price-discounted">${formatMoney(discounted > 0 ? discounted : subtotal)}</span>`;
      if (discountPct > 0 && savings > 0 && settings.showSavings) {
        html += `<span class="jb-bundle__price-badge">Save ${formatMoney(savings)}</span>`;
      }
      html += `</div>`;
      this.pricingEl.innerHTML = html;
    }

    /* ---- CTA ---- */
    updateCTA() {
      const allSelected = this.selections.table && this.selections.chair1 && this.selections.chair2;
      const allAvailable = allSelected && [this.selections.table, this.selections.chair1, this.selections.chair2].every(isProductSelectable);
      this.ctaBtn.disabled = !allSelected || !allAvailable;
      this.ctaBtn.setAttribute('aria-disabled', allAvailable ? 'false' : 'true');
      this.ctaBtn.textContent = allAvailable
        ? this.ctaBtn.dataset.textDefault
        : this.ctaBtn.dataset.textIncomplete;
    }

    /* ---- Add to cart ---- */
    async addToCart() {
      const { table, chair1, chair2 } = this.selections;
      if (!table || !chair1 || !chair2) return;
      if ([table, chair1, chair2].some(item => !isProductSelectable(item))) {
        this.ctaBtn.disabled = true;
        this.ctaBtn.setAttribute('aria-disabled', 'true');
        this.ctaBtn.textContent = 'Sold out';
        return;
      }

      const settings = this.data.settings;

      this.ctaBtn.classList.add('is-loading');
      this.ctaBtn.disabled = true;
      this.ctaBtn.textContent = this.ctaBtn.dataset.textLoading;

      try {
        // If both chairs are the same colour, merge into qty 2 so cart shows one line
        const sameChair = chair1.variantId === chair2.variantId;
        const isPickNMix = [table, chair1, chair2].some(item => (item.title || '').toLowerCase().includes('pick n'));
        const bundleFamily = isPickNMix ? 'pick-n-mix' : 'confetti-set';
        const bundleId = `${bundleFamily}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const bundleProperties = itemType => {
          const properties = {
            '_bundle': bundleFamily,
            '_bundle_id': bundleId,
            '_bundle_item': itemType
          };
          if (settings.preorderMode && settings.preorderPropertyName) {
            properties[settings.preorderPropertyName] = settings.preorderPropertyValue;
          }
          return properties;
        };
        const items = [
          { id: table.variantId, quantity: 1, properties: { ...bundleProperties('table'), '_bundle_discount': `${settings.discountPct}%` } },
          ...(sameChair
            ? [{ id: chair1.variantId, quantity: 2, properties: bundleProperties('chair') }]
            : [
                { id: chair1.variantId, quantity: 1, properties: bundleProperties('chair-1') },
                { id: chair2.variantId, quantity: 1, properties: bundleProperties('chair-2') },
              ]
          ),
        ];

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error('Add to cart failed');

        // Auto-apply discount code if configured
        if (settings.discountCode) {
          await fetch(`/discount/${encodeURIComponent(settings.discountCode)}`).catch(() => {});
        }

        this.ctaBtn.textContent = '✓ Added! Redirecting…';
        this.ctaBtn.classList.remove('is-loading');
        window.location.href = '/cart';

      } catch (err) {
        console.error('[JBBundle] Error:', err);
        this.ctaBtn.classList.remove('is-loading');
        this.ctaBtn.disabled = false;
        this.ctaBtn.textContent = 'Error. Please try again';
        setTimeout(() => { this.ctaBtn.textContent = this.ctaBtn.dataset.textDefault; }, 3000);
      }
    }

    /* ---- Cart drawer refresh (Broadcast-native) ---- */
    refreshCartDrawer() {
      // Use Broadcast's exact internal event names (confirmed from theme.js source).
      // theme:cart:refresh → triggers cart-items.getCart() which re-fetches and re-renders everything
      // theme:cart-drawer:open → opens the drawer
      // We do NOT touch the DOM directly — Broadcast owns that.

      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));

      // Small delay lets getCart() fetch complete before drawer opens
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('theme:cart-drawer:open', {
          bubbles: true,
          detail: { target: document.querySelector('cart-drawer') }
        }));
      }, 350);
    }
  }

  /* ---- Init ---- */
  function initAll() {
    document.querySelectorAll('[id^="jb-bundle-"]').forEach(el => {
      if (!el._jbBundle) el._jbBundle = new JBBundleBuilder(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);

})();
